import { useFormik } from "formik";
import * as Yup from "yup";
import { Card, Form, Button } from "react-bootstrap";
import { useUserDetail } from "../core/QueryResponseProvider";
import { useNavigate, useParams } from "react-router-dom";
import { editUser } from "../core/_request";
import { useToast } from "../../../modules/toast/Toast";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../modules/auth";
import { useQueryResponse } from "../../merchant-management/core/QueryResponseProvider";
import { useQueryRequest } from "../../merchant-management/core/QueryRequestProvider";
import { Merchant } from "../../merchant-management/core/_model";
import { useDebounce } from "../../../../_mcp/helpers";
import clsx from "clsx";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { response } = useUserDetail();
  const { detailData } = response ?? {};
  const { currentUser } = useAuth();

  const loggedInRole = currentUser?.auth?.role ?? "";
  const isLoggedInInternal = loggedInRole.startsWith("INTERNAL");

  // Merchant list context
  const dataMerchant: Merchant[] | any = useQueryResponse();
  const { updateState } = useQueryRequest();
  const merchantData = dataMerchant.response
    ? dataMerchant.response.detailData.docs
    : [];

  // Merchant search state
  const [merchantSearchTerm, setMerchantSearchTerm] = useState<string>("");
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const debouncedMerchantSearch = useDebounce(merchantSearchTerm, 500);

  useEffect(() => {
    if (debouncedMerchantSearch) {
      const filtered = merchantData.filter((m: Merchant) =>
        [m.merchantName, m.merchantBrandName, m._id]
          .filter(Boolean)
          .some((val) =>
            (val as string)
              .toLowerCase()
              .includes(debouncedMerchantSearch.toLowerCase())
          )
      );
      setFilteredMerchants(filtered);
    } else {
      setFilteredMerchants(merchantData);
    }
  }, [debouncedMerchantSearch, merchantData]);

  useEffect(() => {
    updateState({ search: debouncedMerchantSearch ?? "", page: 1, limit: 10 });
  }, [debouncedMerchantSearch, updateState]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true, // Enable real-time validation
    validateOnBlur: true,
    validateOnMount: false, // Don't validate on initial load
    initialValues: {
      fullName: detailData?.fullName || "",
      userName: detailData?.userName || "",
      email: detailData?.email || "",
      merchantId: detailData?.merchantId || "",
      role: detailData?.role || "",
      isActive: detailData?.isActive ?? true,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Full Name is required"),
      userName: Yup.string(), // Username is disabled, no validation needed
      email: Yup.string().email("Invalid email").required("Email is required"),
      role: Yup.string().required("Role is required"),
      merchantId: Yup.string().when("role", {
        is: (val: string) => isLoggedInInternal && val?.startsWith("MERCHANT"),
        then: (schema) =>
          schema.required("Merchant is required for MERCHANT roles"),
        otherwise: (schema) => schema,
      }),
    }),
    onSubmit: async (values) => {
      try {
        const payload: any = {
          _id: id,
          fullName: values.fullName,
          userName: values.userName,
          email: values.email,
          role: values.role,
          isActive: values.isActive,
        };

        // Only include merchantId if it has a value
        if (values.merchantId && values.merchantId !== "") {
          payload.merchantId = values.merchantId;
        }

        const response = await editUser(payload);
        if (response.responseCode === "200") {
          notify("success", "User updated successfully");
          navigate(-1);
        } else {
          notify("error", response.responseMessage);
        }
      } catch (err: any) {
        notify("error", err?.response.data.responseMessage);
      }
    },
  });

  // Clear merchantId when role is INTERNAL_*
  useEffect(() => {
    if (formik.values.role?.startsWith("INTERNAL")) {
      formik.setFieldValue("merchantId", "");
      setMerchantSearchTerm("");
    } else {
      // Pre-fill search input with selected merchant name (if any)
      if (formik.values.merchantId) {
        const m = merchantData.find(
          (mm: any) => mm._id === formik.values.merchantId
        );
        if (m) setMerchantSearchTerm(m.merchantName);
      }
    }
  }, [formik.values.role, formik.values.merchantId, merchantData]);

  const selectedRoleIsInternal = formik.values.role?.startsWith("INTERNAL");

  const handleMerchantSelect = (merchant: Merchant) => {
    formik.setFieldValue("merchantId", merchant._id);
    setMerchantSearchTerm(merchant.merchantName);
    setShowMerchantDropdown(false);
  };

  const getSelectedMerchantName = () => {
    if (!formik.values.merchantId) return "";
    const merchant = merchantData.find(
      (m: Merchant) => m._id === formik.values.merchantId
    );
    return merchant ? merchant.merchantName : "";
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header Section */}
      <div className="container-fluid px-8 mb-6">
        <div className="bg-white rounded-4 shadow-sm p-6 border-0">
          <div className="mb-4">
            <h3 className="fw-bold text-gray-800 mb-2">Edit User</h3>
            <p className="text-muted fs-7 mb-0">Modify user information and account settings</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid px-8 flex-grow-1">
        <form onSubmit={formik.handleSubmit} className="h-100">
          <div className="row g-6">
            {/* User Information Card */}
            <div className="col-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header bg-white py-4" style={{borderBottom: '2px solid #e4e6ef'}}>
                  <div className="d-flex align-items-center">
                    <div className="symbol symbol-35px me-3">
                      <div className="symbol-label bg-primary">
                        <i className="fas fa-user text-white"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="card-title mb-0 fw-bold text-gray-800">User Information</h3>
                      <p className="card-subtitle text-muted fs-6 mb-0">Basic user details and account information</p>
                    </div>
                  </div>
                </div>
                <div className="card-body p-10">
                  <div className="row g-6">
                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Full Name *</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-user text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className={clsx("form-control", {
                            "is-invalid": formik.touched.fullName && formik.errors.fullName,
                          })}
                          placeholder="Enter full name"
                          {...formik.getFieldProps("fullName")}
                        />
                      </div>
                      <div className="invalid-feedback">{formik.errors.fullName}</div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Username *</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-at text-gray-600"></i>
                        </span>
                        <input
                          type="text"
                          className={clsx("form-control", {
                            "is-invalid": formik.touched.userName && formik.errors.userName,
                          })}
                          placeholder="Enter username"
                          disabled={true}
                          {...formik.getFieldProps("userName")}
                        />
                      </div>
                      <div className="form-text text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Username cannot be changed after creation
                      </div>
                      <div className="invalid-feedback">{formik.errors.userName}</div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Email Address *</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-envelope text-gray-600"></i>
                        </span>
                        <input
                          type="email"
                          className={clsx("form-control", {
                            "is-invalid": formik.touched.email && formik.errors.email,
                          })}
                          placeholder="Enter email address"
                          {...formik.getFieldProps("email")}
                        />
                      </div>
                      <div className="invalid-feedback">{formik.errors.email}</div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-700 fw-semibold mb-3">Role *</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">
                          <i className="fas fa-shield-alt text-gray-600"></i>
                        </span>
                        <select
                          className={clsx("form-select", {
                            "is-invalid": formik.touched.role && formik.errors.role,
                          })}
                          {...formik.getFieldProps("role")}
                        >
                          <option value="">Select Role</option>
                          {isLoggedInInternal && (
                            <>
                              <option value="INTERNAL_ADMIN">INTERNAL ADMIN</option>
                              <option value="INTERNAL_MAKER">INTERNAL MAKER</option>
                              <option value="INTERNAL_APPROVAL">INTERNAL APPROVAL</option>
                            </>
                          )}
                          <option value="MERCHANT_ADMIN">MERCHANT ADMIN</option>
                          <option value="MERCHANT_MAKER">MERCHANT MAKER</option>
                          <option value="MERCHANT_APPROVAL">MERCHANT APPROVAL</option>
                        </select>
                      </div>
                      <div className="invalid-feedback">{formik.errors.role as any}</div>
                    </div>

                    <div className="col-12">
                      <div className="form-check form-switch form-check-custom form-check-solid">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isActive"
                          {...formik.getFieldProps("isActive")}
                          checked={formik.values.isActive}
                        />
                        <label className="form-check-label fw-semibold text-gray-700 ms-3" htmlFor="isActive">
                          Account Status - {formik.values.isActive ? "Active" : "Inactive"}
                        </label>
                      </div>
                      <div className="form-text text-muted mt-2">
                        <i className="fas fa-info-circle me-1"></i>
                        Toggle to activate or deactivate the user account
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Merchant Assignment Card - Only for INTERNAL users assigning MERCHANT roles */}
            {isLoggedInInternal && !selectedRoleIsInternal && (
              <div className="col-12">
                <div className="card shadow-sm border-0 rounded-4">
                  <div className="card-header bg-white py-4" style={{borderBottom: '2px solid #e4e6ef'}}>
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-35px me-3">
                        <div className="symbol-label bg-primary">
                          <i className="fas fa-building text-white"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="card-title mb-0 fw-bold text-gray-800">Merchant Assignment</h3>
                        <p className="card-subtitle text-muted fs-6 mb-0">Assign user to a specific merchant</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-10">
                    <div className="row g-6">
                      <div className="col-12">
                        <label className="form-label text-gray-700 fw-semibold mb-3">Merchant *</label>
                        <div className="position-relative">
                          <div className="input-group input-group-lg">
                            <span className="input-group-text">
                              <i className="fas fa-search text-gray-600"></i>
                            </span>
                            <input
                              type="text"
                              className={clsx("form-control", {
                                "is-invalid": formik.touched.merchantId && formik.errors.merchantId,
                              })}
                              placeholder="Search for a merchant..."
                              value={merchantSearchTerm}
                              onChange={(e) => {
                                setMerchantSearchTerm(e.target.value);
                                setShowMerchantDropdown(true);
                                if (!e.target.value) {
                                  formik.setFieldValue("merchantId", "");
                                }
                              }}
                              onFocus={() => setShowMerchantDropdown(true)}
                            />
                          </div>
                          {showMerchantDropdown && (
                            <div
                              className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-2"
                              style={{
                                zIndex: 1000,
                                maxHeight: "250px",
                                overflowY: "auto",
                              }}
                            >
                              {filteredMerchants.length > 0 ? (
                                filteredMerchants.map((merchant: Merchant) => (
                                  <div
                                    key={merchant._id}
                                    className="p-3 cursor-pointer border-bottom border-light hover-bg-light-primary"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleMerchantSelect(merchant)}
                                  >
                                    <div className="d-flex align-items-start">
                                      <div className="symbol symbol-40px me-3">
                                        <div className="symbol-label bg-light-primary">
                                          <i className="fas fa-building text-primary"></i>
                                        </div>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="fw-bold text-gray-800 fs-6">
                                          {merchant.merchantName}
                                        </div>
                                        <div className="text-muted fs-7">
                                          {merchant.merchantBrandName}
                                        </div>
                                        <div className="text-muted fs-8">
                                          ID: {merchant._id}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-4 text-center text-muted">
                                  <i className="fas fa-search fs-2 mb-2"></i>
                                  <div>No merchants found</div>
                                </div>
                              )}
                            </div>
                          )}
                          {formik.values.merchantId && (
                            <div className="mt-3 p-3 bg-light-success rounded-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-30px me-3">
                                  <div className="symbol-label bg-success">
                                    <i className="fas fa-check text-white fs-7"></i>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-800 fw-semibold fs-7">Selected Merchant:</div>
                                  <div className="text-success fw-bold">{getSelectedMerchantName()}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="invalid-feedback">{formik.errors.merchantId as any}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-4 shadow-sm border-0 p-6 mt-6">
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                <div className="fs-7">Ready to save changes?</div>
                <div className="fs-8">All modifications will be validated and updated</div>
              </div>
              <div className="d-flex gap-3">
                <button
                  type="button"
                  className="btn btn-light btn-lg px-8"
                  onClick={() => navigate(-1)}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-lg px-8 shadow-primary d-flex align-items-center"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
